import { Icon } from "materia"
import { type CSSProperties, type ReactNode, useId } from "react"
import { useTranslation } from "react-i18next"
import { modifiers } from "ui-tools"
import { JourneyFolderIcon } from "../JourneyFolderIcon"
import { CompletedCheck } from "./CompletedCheck"
import {
  type BoxEdges,
  getFolderJourneys,
  getIsJourneyCompleted,
} from "./getJourneyListItems"
import type { JourneyFolder } from "./gql"
import { type ConnectorType, JourneyConnector } from "./JourneyConnector"

import s from "./YourJourneysList.module.scss"

type JourneyFolderListItemProps = {
  folder: JourneyFolder
  connectorType: ConnectorType
  completedBox: BoxEdges | null
  isExpanded: boolean
  activeJourneyId: string | undefined
  onSelect: () => void
  children: ReactNode
}
export const JourneyFolderListItem = ({
  folder,
  connectorType,
  completedBox,
  isExpanded,
  activeJourneyId,
  onSelect,
  children,
}: JourneyFolderListItemProps) => {
  const { t } = useTranslation()
  const journeysId = useId()

  const title = folder.title ?? ""
  const journeys = getFolderJourneys(folder)
  const journeyCount = journeys.length
  const completedJourneyCount = journeys.filter(getIsJourneyCompleted).length
  const isCompleted = completedJourneyCount === journeyCount
  // An open folder keeps its chevron, so it shows how to close it again
  const showsCompleted = isCompleted && !isExpanded
  // The line from the folder leads to its current journey, or to the first one
  const connectedJourneyIndex = Math.max(
    journeys.findIndex((journey) => journey.id === activeJourneyId),
    0
  )

  return (
    <div
      className={modifiers(s, "journeyItem", {
        isExpanded,
        hasCompletedBox: Boolean(completedBox),
        isBoxStart: Boolean(completedBox?.isStart),
        isBoxEnd: Boolean(completedBox?.isEnd),
      })}
    >
      {connectorType && <JourneyConnector type={connectorType} />}

      <div className={s.journeyItem__row}>
        <button
          type="button"
          className={modifiers(s, "journeyItem__button", {
            hasTrailingIcon: true,
          })}
          onClick={onSelect}
          title={title}
          aria-expanded={isExpanded}
          aria-controls={journeysId}
          aria-label={`${title}, ${t("journey.FOLDER_JOURNEYS_COMPLETED", {
            completed: completedJourneyCount,
            total: journeyCount,
          })}`}
        >
          <div className={s.journeyItem__thumbnailColumn}>
            <JourneyFolderIcon isCompleted={isCompleted} />
          </div>

          <div
            className={modifiers(s, "journeyItem__title", {
              isFolder: true,
              isCertified: showsCompleted,
            })}
          >
            {title}
          </div>
        </button>

        <div className={s.journeyItem__trailingIcon} aria-hidden>
          {showsCompleted ? (
            <CompletedCheck />
          ) : (
            <div
              className={modifiers(s, "journeyItem__chevron", { isExpanded })}
            >
              <Icon icon="Chevron" size="small" />
            </div>
          )}
        </div>
      </div>

      {/* biome-ignore lint/a11y/useSemanticElements: fieldset is for form fields, this only groups a folder's journeys */}
      <div
        id={journeysId}
        className={s.journeyItem__folderJourneys}
        role="group"
        aria-label={t("journey.FOLDER_JOURNEYS", { folder: title })}
        hidden={!isExpanded}
      >
        {isExpanded && (
          <>
            <div
              className={s.journeyItem__folderConnector}
              style={
                {
                  "--connected-journey-index": connectedJourneyIndex,
                } as CSSProperties
              }
              aria-hidden
            />
            {children}
          </>
        )}
      </div>
    </div>
  )
}
